import React, { useEffect, useState } from "react";
import { Loader2, Copy, Sparkles, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LangPicker } from "@/components/language/LangPicker";

const PLATFORMS = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "twitter", label: "X / Twitter" },
  { id: "tiktok", label: "TikTok" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "youtube", label: "YouTube" },
];

export const LangSocialTab = ({ overview, stationId }) => {
  const [platforms, setPlatforms] = useState(["facebook", "instagram", "twitter"]);
  const [topic, setTopic] = useState("");
  const [langs, setLangs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => { if (overview) setLangs(overview.active_languages || []); }, [overview]);

  const togglePlatform = (p) => setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const toggleLang = (c) => setLangs((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const generate = async () => {
    if (!topic.trim()) return toast.error("Type a topic first");
    if (!platforms.length || !langs.length) return toast.error("Pick at least one platform and language");
    setBusy(true); setResults([]);
    try {
      const r = await api.post("/language-center/social/localize", {
        station_id: stationId, topic, platforms, languages: langs });
      setResults(r.data.items || []);
      toast.success("Posts localized");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  const platformLabel = (id) => PLATFORMS.find((p) => p.id === id)?.label || id;

  return (
    <div data-testid="lc-social-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">Social Media Localization</h2>
      <p className="text-sm text-ink-secondary mb-6">One story → Posts for every platform in every language</p>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Platforms</div>
            <div className="space-y-1">
              {PLATFORMS.map((p) => {
                const on = platforms.includes(p.id);
                return (
                  <button key={p.id} onClick={() => togglePlatform(p.id)} data-testid={`lc-platform-${p.id}`}
                    className={`w-full flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition ${
                      on ? "bg-brand-amber/10 border border-brand-amber/40 text-ink-primary" : "border border-transparent text-ink-secondary hover:bg-bg-elevated"}`}>
                    <span className="flex-1">{p.label}</span>
                    {on && <Check className="h-3.5 w-3.5 text-brand-amber" />}
                  </button>
                );
              })}
            </div>
          </div>
          <LangPicker all={overview?.languages?.all || []} selected={langs} onToggle={toggleLang} title="Languages" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">Content Source</div>
          <textarea
            value={topic} onChange={(e) => setTopic(e.target.value)} rows={4} data-testid="lc-social-topic"
            placeholder="Type your topic, announcement, or story…"
            className="w-full rounded-xl border border-line bg-bg-surface p-4 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/50"
          />
          <button
            onClick={generate} disabled={busy} data-testid="lc-social-generate-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Generating…" : `Generate ${platforms.length} Platform${platforms.length === 1 ? "" : "s"} × ${langs.length} Language${langs.length === 1 ? "" : "s"}`}
          </button>

          <div className="mt-6 space-y-5">
            {results.map((r) => (
              <div key={r.language} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`lc-social-result-${r.language}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-mono text-[9px] uppercase rounded bg-bg-elevated px-1.5 py-0.5 text-ink-secondary">{r.language}</span>
                  <span className="text-sm font-semibold text-ink-primary">{r.name}</span>
                  <span className="text-xs text-ink-muted">{r.native}</span>
                </div>
                {r.error ? (
                  <div className="text-xs text-rose-400">Failed: {r.error}</div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(r.posts || {}).map(([pid, text]) => (
                      <div key={pid} className="rounded-lg border border-line bg-bg-elevated p-3">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-sky-300">{platformLabel(pid)}</span>
                          <span className="flex-1" />
                          <button onClick={() => copy(text)} className="inline-flex items-center gap-1 text-[10px] text-ink-muted hover:text-ink-primary transition">
                            <Copy className="h-3 w-3" /> Copy
                          </button>
                        </div>
                        <div className="whitespace-pre-wrap text-sm text-ink-secondary">{text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
